import React, { useEffect, useRef, useState } from 'react';
import { fromLonLat } from 'ol/proj';
import { trackFunnelStep, FunnelSteps } from '../../lib/analytics';
import './MapSearch.css';

const MapSearch = ({ mapRef, onResultSelected, onExpandedChange }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const inputRef = useRef(null);
    const debounceTimeoutRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (isExpanded && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isExpanded]);

    const closeSearch = () => {
        setIsExpanded(false);
        setQuery('');
        setResults([]);
        onExpandedChange?.(false);
        trackFunnelStep(FunnelSteps.LOCATION_SEARCH_CLOSED);
    };

    const handleButtonClick = () => {
        if (!isExpanded) {
            setIsExpanded(true);
            onExpandedChange?.(true);
            trackFunnelStep(FunnelSteps.LOCATION_SEARCH_OPENED);
        } else {
            closeSearch();
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value; // No hacer trim aquí para permitir espacios
        setQuery(value);

        // Limpiar timeout anterior
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        const trimmedValue = value.trim();
        if (trimmedValue.length < 3) {
            setResults([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);

        // Debounce de 500ms - usar el valor trimmed para la búsqueda
        debounceTimeoutRef.current = setTimeout(() => {
            searchAddress(trimmedValue);
        }, 500);
    };

    const searchAddress = async (searchQuery) => {
        try {
            const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=20&countrycodes=ar&accept-language=es`;
            
            const response = await fetch(url, {
                headers: {
                    'User-Agent': 'Specific-AG Landing Page'
                }
            });
            
            const data = await response.json();
            setResults(data);
            setIsSearching(false);
        } catch (error) {
            console.error('Error en la búsqueda:', error);
            setIsSearching(false);
            setResults([]);
        }
    };

    const parseDisplayName = (displayName) => {
        // Parsear display_name como: "Las Higueras, Municipio de Las Higueras, Pedanía Río Cuarto, Departamento Río Cuarto, Córdoba, X5800, Argentina"
        const parts = displayName.split(',').map(p => p.trim()).filter(p => p);
        const parsed = {
            name: parts[0] || '',
            municipality: '',
            pedania: '',
            department: '',
            city: '',
            postcode: '',
            state: '',
            country: ''
        };

        // Buscar provincia/estado (generalmente el penúltimo antes del código postal o país)
        // Buscar código postal (generalmente un código alfanumérico)
        // Buscar país (generalmente el último)
        
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            
            // Identificar código postal (generalmente alfanumérico y corto)
            if (/^[A-Z0-9]{4,8}$/.test(part)) {
                parsed.postcode = part;
            }
            // Identificar país (último elemento o "Argentina", "Chile", etc.)
            else if (i === parts.length - 1 || ['Argentina', 'Chile', 'Uruguay', 'Paraguay', 'Bolivia', 'Brasil', 'Brazil'].includes(part)) {
                parsed.country = part;
            }
            // Identificar provincia/estado (Córdoba, Buenos Aires, etc. - generalmente antes del código postal)
            else if (['Córdoba', 'Buenos Aires', 'Santa Fe', 'Mendoza', 'Tucumán', 'Entre Ríos', 'Salta', 'Misiones', 'Chaco', 'Corrientes', 'Santiago del Estero', 'San Juan', 'Jujuy', 'Río Negro', 'Formosa', 'Neuquén', 'Chubut', 'San Luis', 'Catamarca', 'La Rioja', 'La Pampa', 'Santa Cruz', 'Tierra del Fuego'].some(prov => part.includes(prov))) {
                parsed.state = part;
            }
            // Identificar municipio
            else if (part.toLowerCase().includes('municipio')) {
                parsed.municipality = part.replace(/^municipio de /i, '');
            }
            // Identificar pedanía
            else if (part.toLowerCase().includes('pedanía')) {
                parsed.pedania = part.replace(/^pedanía /i, '');
            }
            // Identificar departamento
            else if (part.toLowerCase().includes('departamento')) {
                parsed.department = part.replace(/^departamento /i, '');
            }
            // Si no es ninguno de los anteriores y no es el primero, podría ser ciudad
            else if (i > 0 && !parsed.city) {
                parsed.city = part;
            }
        }

        return parsed;
    };

    const formatAddress = (result) => {
        const parts = [];
        const addr = result.address || {};
        
        // Si no hay objeto address, parsear display_name
        let city = addr.city || addr.municipality || '';
        let state = addr.state || addr.region || '';
        let country = addr.country || '';
        let locality = addr.locality || addr.village || addr.town || '';
        let postcode = addr.postcode || '';
        let department = addr.county || '';
        let municipality = '';
        
        // Si no hay address object, parsear display_name
        if (!result.address && result.display_name) {
            const parsed = parseDisplayName(result.display_name);
            city = city || parsed.city;
            state = state || parsed.state;
            country = country || parsed.country;
            postcode = postcode || parsed.postcode;
            department = department || parsed.department || parsed.pedania || '';
            municipality = municipality || parsed.municipality || '';
            // Si no hay locality en address, usar el nombre parseado
            if (!locality) {
                locality = parsed.name;
            }
        }
        
        // Primero: Nombre del lugar o dirección principal
        const name = result.name || '';
        let road = addr.road || addr.street || '';
        let houseNumber = addr.house_number || '';
        
        // Caso especial: si el name es solo un número y hay road, el número es house_number
        if (!houseNumber && /^\d+$/.test(name) && road) {
            houseNumber = name;
        }
        
        // Si no hay road pero el nombre contiene calle y número juntos
        // Ejemplo: "Constitucion 452" en el nombre
        if (!road && name && /\d/.test(name)) {
            const addressMatch = name.match(/^(.+?)\s+(\d+)$/);
            if (addressMatch) {
                road = addressMatch[1].trim();
                houseNumber = addressMatch[2].trim();
            }
        }
        
        // Si aún no hay road y el display_name comienza con algo que parece una dirección
        if (!road && result.display_name && /\d/.test(result.display_name)) {
            const displayParts = result.display_name.split(',').map(p => p.trim()).filter(p => p);
            if (displayParts.length > 0) {
                const firstPart = displayParts[0];
                // Intentar extraer calle y número del primer elemento
                const addressMatch = firstPart.match(/^(.+?)\s+(\d+)$/);
                if (addressMatch) {
                    road = addressMatch[1].trim();
                    houseNumber = addressMatch[2].trim();
                } else if (/^\d+$/.test(firstPart) && displayParts.length > 1) {
                    // Si el primer elemento es solo un número, el segundo podría ser la calle
                    houseNumber = firstPart;
                    road = displayParts[1];
                }
            }
        }
        
        // Determinar qué mostrar como información principal
        // PRIORIDAD: Si hay calle Y número, ambos van juntos en negro como primary
        if (road && houseNumber) {
            // Dirección completa: calle y número juntos en negro
            parts.push({ type: 'primary', text: `${road} ${houseNumber}` });
        } else if (road) {
            // Solo calle (sin número)
            parts.push({ type: 'primary', text: road });
        } else if (houseNumber && !road) {
            // Solo número sin calle - no debería pasar, pero por si acaso
            // Intentar buscar la calle en el display_name
            if (result.display_name) {
                const displayParts = result.display_name.split(',').map(p => p.trim()).filter(p => p);
                // El segundo elemento después del número podría ser la calle
                const numberIndex = displayParts.findIndex(p => p === houseNumber);
                if (numberIndex >= 0 && displayParts[numberIndex + 1]) {
                    road = displayParts[numberIndex + 1];
                    parts.push({ type: 'primary', text: `${road} ${houseNumber}` });
                } else {
                    parts.push({ type: 'primary', text: houseNumber });
                }
            } else {
                parts.push({ type: 'primary', text: houseNumber });
            }
        } else if (name && name !== road && name !== addr.road && name !== addr.street && !/^\d+$/.test(name)) {
            // Si hay un nombre de lugar específico (POI, localidad, etc.) que no es solo un número
            parts.push({ type: 'primary', text: name });
        } else if (locality) {
            // Si solo hay localidad, usarla como principal
            parts.push({ type: 'primary', text: locality });
        } else if (city) {
            // Si solo hay ciudad, usarla como principal
            parts.push({ type: 'primary', text: city });
        } else if (result.display_name) {
            // Fallback: usar el primer elemento del display_name
            const displayParts = result.display_name.split(',').map(p => p.trim()).filter(p => p);
            if (displayParts.length > 0) {
                parts.push({ type: 'primary', text: displayParts[0] });
            }
        }
        
        // Segundo: Siempre mostrar contexto (localidad, ciudad, departamento, provincia, país) en gris
        // IMPORTANTE: No mostrar la calle aquí si ya está en el primary
        
        // Si la localidad es diferente y no es el primary, mostrarla
        if (locality && 
            parts[0]?.text !== locality && 
            parts[0]?.text !== name && 
            !parts[0]?.text.includes(locality) &&
            locality !== road) {
            parts.push({ type: 'context', text: locality });
        }
        
        // Ciudad (si existe y no es el primary, y no es la calle)
        if (city && 
            parts[0]?.text !== city && 
            city !== locality && 
            !parts[0]?.text.includes(city) &&
            city !== road) {
            parts.push({ type: 'context', text: city });
        }
        
        // Departamento/Municipio (nivel intermedio después de ciudad)
        if (department && !parts.some(p => p.text === department || p.text.includes(department))) {
            parts.push({ type: 'context', text: department });
        }
        if (municipality && !parts.some(p => p.text === municipality || p.text.includes(municipality))) {
            parts.push({ type: 'context', text: municipality });
        }
        
        // Provincia/Estado (siempre si existe)
        if (state && !parts.some(p => p.text === state || p.text.includes(state))) {
            parts.push({ type: 'context', text: state });
        }
        
        // País (siempre si existe)
        if (country && !parts.some(p => p.text === country || p.text.includes(country))) {
            parts.push({ type: 'context', text: country });
        }

        // Si no hay partes después de todo esto, parsear display_name completo
        if (parts.length === 0 && result.display_name) {
            const parsed = parseDisplayName(result.display_name);
            if (parsed.name) {
                parts.push({ type: 'primary', text: parsed.name });
            }
            if (parsed.city && parsed.city !== parsed.name) {
                parts.push({ type: 'context', text: parsed.city });
            }
            if (parsed.department) {
                parts.push({ type: 'context', text: parsed.department });
            }
            if (parsed.state) {
                parts.push({ type: 'context', text: parsed.state });
            }
            if (parsed.country) {
                parts.push({ type: 'context', text: parsed.country });
            }
        }

        return parts;
    };

    const handleSelectResult = (result) => {
        if (!mapRef || !mapRef.current) return;

        const olMap = mapRef.current;
        const view = olMap.getView();
        const coordinate = fromLonLat([parseFloat(result.lon), parseFloat(result.lat)]);
        
        view.animate({
            center: coordinate,
            zoom: 14,
            duration: 1000,
        });

        setTimeout(() => closeSearch(), 100);

        trackFunnelStep(FunnelSteps.LOCATION_SEARCH_RESULT, {
          query: query.trim().slice(0, 80),
        });

        // Optionally notify parent (e.g. to activate polygon drawing tool)
        if (typeof onResultSelected === 'function') {
            onResultSelected(result);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            closeSearch();
        } else if (e.key === 'Enter' && results.length > 0) {
            handleSelectResult(results[0]);
        }
    };

    const handleBlur = (e) => {
        // Retrasar el colapso para permitir clicks en los resultados
        setTimeout(() => {
            if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
                closeSearch();
            }
        }, 200);
    };

    // Cleanup del debounce
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className={`map-search-container${isExpanded ? ' is-expanded' : ''}`} ref={containerRef}>
            <button
                type="button"
                className={`map-search-button map-control-button ${isExpanded ? 'hidden' : ''}`}
                onClick={handleButtonClick}
                title="Buscar ubicación"
            >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" fill="currentColor"/>
                </svg>
                <span className="map-control-label">Buscar ubicación</span>
            </button>
            <div className={`map-search-input-container ${isExpanded ? 'expanded' : ''}`}>
                <input
                    ref={inputRef}
                    type="text"
                    className="map-search-input"
                    placeholder="Buscar ubicación..."
                    value={query}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                />
                {isSearching && (
                    <div className="map-search-spinner"></div>
                )}
                {isExpanded && (
                    <button
                        type="button"
                        className="map-search-close"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={closeSearch}
                        title="Cerrar búsqueda"
                        aria-label="Cerrar búsqueda"
                    >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" fill="currentColor"/>
                        </svg>
                    </button>
                )}
                {results.length > 0 && (
                    <ul className="map-search-results">
                        {results.map((result, index) => {
                            const addressParts = formatAddress(result);
                            return (
                                <li
                                    key={index}
                                    className="map-search-result"
                                    onClick={() => handleSelectResult(result)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSelectResult(result);
                                        }
                                    }}
                                    tabIndex={0}
                                >
                                    <div className="map-search-address-hierarchical">
                                        {addressParts.map((part, partIndex) => (
                                            <div
                                                key={partIndex}
                                                className={`map-search-address-part map-search-address-${part.type}`}
                                            >
                                                {part.text}
                                            </div>
                                        ))}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
                {query.trim().length >= 3 && results.length === 0 && !isSearching && (
                    <ul className="map-search-results">
                        <li className="map-search-result map-search-no-results">
                            No se encontraron resultados
                        </li>
                    </ul>
                )}
            </div>
        </div>
    );
};

export default MapSearch;
